import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Switch, CircularProgress } from '@material-ui/core';
import { ConfirmDialog } from 'components';
import {
  addTasksToQueueApi,
  updateQuoteProductSubscriptionOptionApi,
  deleteQuoteProductSubscriptionOptionApi
} from 'api';
import useProducts from 'hooks/useProducts';
import SubscriptionAttributeSelect from '../Subscriptions/SubscriptionAttributeSelect';
import SubscriptionAttributeInput from '../Subscriptions/SubscriptionAttributeInput';
import EditMembershipModal from './EditMembershipModal';
import MembershipOptionsTable from './MembershipOptionsTable';
import AddMembershipModal from './AddMembershipModal';
import AddMembershipButton from './AddMembershipButton';


const MembershipsSubscriptions = () => {
  const dispatch = useDispatch();

  const [membershipAttributes, setMembershipAttributes] = useState({
    isMembershipActive: false,
    isOneTimeEnable: false,
    membershipLabel: '',
    minimumTimePeriod: '',
    rebillDate: '',
    firstPeriodPriceLabel: '',
    customerIncludeToGroup: '',
    customerExcludeToGroup: '',
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [editableMembership, setEditableMembership] = useState(null);
  const [openAddingMembershipsDialog, setOpenAddingMembershipsDialog] = useState(false);

  const {
    isMembershipActive,
    isOneTimeEnable,
    membershipLabel,
    firstPeriodPriceLabel,
    minimumTimePeriod,
    rebillDate,
    customerIncludeToGroup,
    customerExcludeToGroup,
  } = membershipAttributes;

  const {
    editableQuoteProduct,
    quoteProductSubscriptionOptions,
    quoteProProductAttributeByCode,
    quoteSubscriptionOptionsLoading,
    loadingQuoteProductAttributes
  } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    const subscriptionAttributes = editableQuoteProduct?.custom_attributes?.reduce((acc, attr) => {
      if (attr.attribute_code === 'subscription_active') {
        return {...acc, isMembershipActive: attr.value == 1};
      }

      if (attr.attribute_code === 'subscription_allow_onetime') {
        return {...acc, isOneTimeEnable: attr.value == 1};
      }

      if (attr.attribute_code === 'subscription_label') {
        return {...acc, membershipLabel: attr.value};
      }

      if (attr.attribute_code === 'membership_label2') {
        return {...acc, firstPeriodPriceLabel: attr.value};
      }

      if (attr.attribute_code === 'minimum_time_period') {
        return {...acc, minimumTimePeriod: attr.value};
      }

      if (attr.attribute_code === 'rebill_date') {
        return {...acc, rebillDate: attr.value};
      }

      if (attr.attribute_code === 'subscription_include_to_group') {
        return {...acc, customerIncludeToGroup: attr.value};
      }

      if (attr.attribute_code === 'subscription_exclude_to_group') {
        return {...acc, customerExcludeToGroup: attr.value};
      }

      return acc;
    }, {});
    setMembershipAttributes((prevState) => ({...prevState, ...subscriptionAttributes}));
  }, [editableQuoteProduct]);

  const { loadingTasksToQueue } = useSelector(
    (state: IReduxState) => state.products
  );
  const { customerGroups } = useSelector(
    (state: IReduxState) => state.customers
  );
  const {
    profile: { stores },
  } = useSelector((state: IReduxState) => state.user);
  const [{ quoteProStoreCode }] = stores;

  const {
    queueTasksSkus
  } = useProducts(quoteProStoreCode);

  const isTaskInQueue = queueTasksSkus.find((sku) => sku === editableQuoteProduct?.sku);

  const isLoading =
    quoteSubscriptionOptionsLoading ||
    isTaskInQueue ||
    loadingTasksToQueue ||
    loadingQuoteProductAttributes;

  const createOneTimePurchaseOption = () => {
    const option = [{
      product_id: editableQuoteProduct.id,
      frequency_count: 0,
      frequency_unit: null,
      length: null,
      installment_price: null,
      adjustment_price: null,
      sproduct_label: null,
      ssort_order: 0
    }];

    const productToUpdate = {
      sku: editableQuoteProduct.sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '1'
      }]
    };

    const task = {
      body: productToUpdate,
      name: editableQuoteProduct.name,
      sku: editableQuoteProduct.sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: quoteProStoreCode
    };

    updateQuoteProductSubscriptionOptionApi({
      dispatch,
      subscriptions: option,
      isNewOptions: true
    });
    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const deleteOneTimePurchaseOption = async () => {
    const id = quoteProductSubscriptionOptions?.find((option) => option.frequency_count == 0)?.id;

    if (id) {
      await deleteQuoteProductSubscriptionOptionApi({dispatch, id});
    }

    const productToUpdate = {
      sku: editableQuoteProduct.sku,
      custom_attributes: [{
        attribute_code: 'subscription_allow_onetime',
        value: '0'
      }]
    };
    const task = {
      body: productToUpdate,
      name: editableQuoteProduct.name,
      sku: editableQuoteProduct.sku,
      requestType: 'UPDATE_PRODUCT',
      storeViewCode: quoteProStoreCode
    };
    addTasksToQueueApi({dispatch, tasks: [task]});
  };

  const handleChangeMembershipStatus = (checked) => {
    const isMembershipEnable = editableQuoteProduct.custom_attributes?.find(
      (a) => a.attribute_code === 'subscription_active')?.value == 1;

    const isOneTimeEnabled = editableQuoteProduct.custom_attributes?.find(
      (a) => a.attribute_code === 'subscription_allow_onetime')?.value == 1;

    const isNoOptions = quoteProductSubscriptionOptions.length === 0 && !isMembershipEnable;
    const isOneTimeEnableWithoutOptions = quoteProductSubscriptionOptions.length === 1 &&
      isOneTimeEnabled &&
      !isMembershipEnable;

    if (isOneTimeEnableWithoutOptions && isNoOptions) {
      setOpenConfirmDialog(true);
    } else {
      const productToUpdate = {
        sku: editableQuoteProduct?.sku,
        custom_attributes: [
          { attribute_code: 'subscription_active', value: checked ? 1 : 0 },
        ],
      };
      const task = {
        body: productToUpdate,
        name: editableQuoteProduct?.name,
        sku: editableQuoteProduct?.sku,
        requestType: 'UPDATE_PRODUCT',
        storeViewCode: quoteProStoreCode,
      };
      addTasksToQueueApi({ dispatch, tasks: [task] });
    }
  };

  const handleSaveMembershipOption = async (values) => {
    setOpenAddingMembershipsDialog(false);
    setEditableMembership(null);

    await updateQuoteProductSubscriptionOptionApi({
      dispatch,
      subscriptions: values?.editedMembershipOption
        ? values.editedMembershipOption
        : values,
      isNewOptions: !values?.editedMembershipOption
    });
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={3}>
        Enable:
      </Grid>
      <Grid item xs={9}>
        {isLoading ? (
          <CircularProgress className="ml-2" />
        ) : (
          <Switch
            color="primary"
            name="subscription_active"
            disabled={isLoading}
            checked={isMembershipActive}
            onClick={(event) => {
              event.stopPropagation();
              const { checked } = event.target as HTMLInputElement;
              handleChangeMembershipStatus(checked);
            }}
          />
        )}
      </Grid>

      <Grid item xs={3}>Allow One Time Purchase:</Grid>
      <Grid item xs={9}>
        {isLoading
          ? <CircularProgress className="ml-2" />
          :
          <Switch
            color="primary"
            name="subscription_allow_onetime"
            disabled={isLoading}
            checked={isOneTimeEnable}
            onChange={({target: {checked}}) => checked
              ? createOneTimePurchaseOption()
              : deleteOneTimePurchaseOption()
            }
          />
        }
      </Grid>
      <SubscriptionAttributeInput
        label="Membership Subscription Label:"
        attributeCode="subscription_label"
        savedValue={membershipLabel}
        isLoading={isLoading}
      />
      <SubscriptionAttributeInput
        label="First Period Price Label:"
        attributeCode="membership_label2"
        savedValue={firstPeriodPriceLabel}
        isLoading={isLoading}
      />
      <SubscriptionAttributeInput
        label="Minimum Time Period (days):"
        attributeCode="minimum_time_period"
        savedValue={minimumTimePeriod}
        isLoading={isLoading}
      />
      <SubscriptionAttributeSelect
        label="Rebill Date:"
        attributeCode="rebill_date"
        savedValue={rebillDate}
        selectOptionsData={quoteProProductAttributeByCode?.['rebill_date'] || []}
        isLoading={isLoading}
      />
      <SubscriptionAttributeSelect
        label="On subscription, move to group:"
        attributeCode="subscription_include_to_group"
        savedValue={customerIncludeToGroup}
        selectOptionsData={customerGroups}
        isLoading={isLoading}
      />
      <SubscriptionAttributeSelect
        label="On unsubscription/expiry, move to group:"
        attributeCode="subscription_exclude_to_group"
        savedValue={customerExcludeToGroup}
        selectOptionsData={customerGroups}
        isLoading={isLoading}
      />
      <Grid item xs={12}>Subscription Options:</Grid>
      {isLoading
        ? <CircularProgress className="ml-2" />
        :
        <MembershipOptionsTable
          setEditableMembership={setEditableMembership}
        />
      }
      <Grid item xs={12}>
        {!isLoading &&
          <AddMembershipButton
            setOpenAddingMembershipsDialog={setOpenAddingMembershipsDialog}
          />
        }
        <AddMembershipModal
          openAddingMembershipsDialog={openAddingMembershipsDialog}
          setOpenAddingMembershipsDialog={setOpenAddingMembershipsDialog}
          handleSaveMembershipOption={handleSaveMembershipOption}
        />
        <EditMembershipModal
          editableMembership={editableMembership}
          setEditableMembership={setEditableMembership}
          handleSaveMembershipOption={handleSaveMembershipOption}
        />
        <ConfirmDialog
          title="Do you want to enable Membership?"
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            setOpenConfirmDialog(false);
            setOpenAddingMembershipsDialog(true);
          }}
        >
          Firstly add a subscription options to enable subscription for this product
        </ConfirmDialog>
      </Grid>
    </Grid>
  );
};

export default MembershipsSubscriptions;
